import { useState } from 'react';

import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { inputCls } from '@/components/ui/form';
import { useAppData } from '@/app-data';
import * as api from '@/features/backlog/api';
import { Board } from '@/features/backlog/types';

export const ManageBoardsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { boards, reloadBoards } = useAppData();
  const [names, setNames] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Board | null>(null);

  const nameOf = (b: Board) => names[b.id] ?? b.name;

  const save = async (b: Board) => {
    setBusy(true);
    try {
      await api.updateBoard(b.id, { name: nameOf(b), position: b.position });
      await reloadBoards();
    } finally {
      setBusy(false);
    }
  };
  const doDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.deleteBoard(pendingDelete.id);
      await reloadBoards();
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.createBoard({ name: newName.trim() });
      setNewName('');
      await reloadBoards();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title="Manage boards">
      <div className="space-y-2">
        {boards.map((b) => (
          <div key={b.id} className="flex items-center gap-2">
            <input
              className={inputCls}
              value={nameOf(b)}
              onChange={(e) => setNames((s) => ({ ...s, [b.id]: e.target.value }))}
            />
            <Button variant="outline" disabled={busy} onClick={() => save(b)}>
              Save
            </Button>
            <Button variant="danger" disabled={busy || boards.length <= 1} onClick={() => setPendingDelete(b)}>
              Delete
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-2 border-t border-line pt-3">
          <input
            className={inputCls}
            placeholder="New board name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <Button variant="primary" disabled={busy} onClick={add}>
            Add
          </Button>
        </div>
        </div>
      </Modal>
      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={doDelete}
        title="Delete board?"
        message={
          pendingDelete
            ? `“${pendingDelete.name}” and all of its items will be permanently removed. This can’t be undone.`
            : undefined
        }
        confirmLabel="Delete"
        busy={busy}
      />
    </>
  );
};

export default ManageBoardsModal;
